import React from 'react';
import { StatCard } from '@/components/dashboard/analytics/StatCard';
import { useKPIMetricsOptimized } from '@/hooks/useKPIMetricsOptimized';
import { useBookingAnalytics } from '@/hooks/useBookingAnalytics';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, BarChart3, TrendingUp, Users, BedDouble, DollarSign, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChatAnalyticsWidgetProps {
  type: 'overview' | 'revenue' | 'occupancy' | 'kpi' | 'bookings';
  dateFrom?: string;
  dateTo?: string;
  onNavigate?: () => void;
}

export const ChatAnalyticsWidget: React.FC<ChatAnalyticsWidgetProps> = ({
  type,
  dateFrom,
  dateTo,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const from = dateFrom || today;
  const to = dateTo || today;

  const { data: kpiData, loading: kpiLoading } = useKPIMetricsOptimized({ dateFrom: from, dateTo: to });
  const { summary, loading: bookingLoading } = useBookingAnalytics({ dateFrom: from, dateTo: to });

  const loading = kpiLoading || bookingLoading;

  if (loading) {
    return (
      <Card className="mt-2 p-4 bg-accent/20">
        <div className="flex items-center justify-center gap-2 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Загрузка аналитики...</span>
        </div>
      </Card>
    );
  }

  // Extract KPI values
  const revenue = kpiData.find(m => m.metric_name === 'Total Revenue');
  const occupancy = kpiData.find(m => m.metric_name === 'Occupancy Rate');
  const adr = kpiData.find(m => m.metric_name === 'ADR');
  const totalBookings = kpiData.find(m => m.metric_name === 'Total Bookings');

  const formatValue = (val: number, unit: string) => {
    if (unit === 'RUB') return `${Math.round(val).toLocaleString('ru-RU')} ₽`;
    if (unit === '%') return `${Math.round(val)}%`;
    return val.toString();
  };

  const getChange = (metric?: typeof revenue) => {
    if (!metric || !metric.previous_value) return undefined;
    const diff = metric.metric_value - metric.previous_value;
    return metric.previous_value > 0 ? Math.round((diff / metric.previous_value) * 100) : undefined;
  };

  const handleOpenAnalytics = () => {
    onNavigate?.();
    navigate(`/admin/analytics?from=${from}&to=${to}`);
  };

  return (
    <Card className="mt-2 p-3 bg-gradient-to-br from-accent/30 to-accent/10 border-accent/50">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Аналитика</span>
        <span className="text-xs text-muted-foreground ml-auto">
          {from === to ? new Date(from).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) : `${new Date(from).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} — ${new Date(to).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(type === 'overview' || type === 'revenue' || type === 'kpi') && revenue && (
          <div className="rounded-lg bg-background/60 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] text-muted-foreground">Выручка</span>
            </div>
            <div className="text-sm font-bold">{formatValue(revenue.metric_value, revenue.unit)}</div>
            {getChange(revenue) !== undefined && (
              <div className={`text-[10px] ${getChange(revenue)! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {getChange(revenue)! >= 0 ? '↑' : '↓'} {Math.abs(getChange(revenue)!)}%
              </div>
            )}
          </div>
        )}

        {(type === 'overview' || type === 'occupancy' || type === 'kpi') && occupancy && (
          <div className="rounded-lg bg-background/60 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <BedDouble className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] text-muted-foreground">Загрузка</span>
            </div>
            <div className="text-sm font-bold">{formatValue(occupancy.metric_value, occupancy.unit)}</div>
            {getChange(occupancy) !== undefined && (
              <div className={`text-[10px] ${getChange(occupancy)! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {getChange(occupancy)! >= 0 ? '↑' : '↓'} {Math.abs(getChange(occupancy)!)}%
              </div>
            )}
          </div>
        )}

        {(type === 'overview' || type === 'kpi') && adr && (
          <div className="rounded-lg bg-background/60 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] text-muted-foreground">ADR</span>
            </div>
            <div className="text-sm font-bold">{formatValue(adr.metric_value, adr.unit)}</div>
          </div>
        )}

        {(type === 'overview' || type === 'bookings') && (
          <div className="rounded-lg bg-background/60 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] text-muted-foreground">Брони</span>
            </div>
            <div className="text-sm font-bold">{summary.totalBookings}</div>
            <div className="text-[10px] text-muted-foreground">
              {summary.confirmedBookings} подтв.
            </div>
          </div>
        )}
      </div>

      <Button
        size="sm"
        variant="outline"
        className="w-full mt-3 h-7 text-xs"
        onClick={handleOpenAnalytics}
      >
        <ExternalLink className="h-3 w-3 mr-1" />
        Открыть полную аналитику
      </Button>
    </Card>
  );
};